import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSection from '../components/ui/page-section';
import SectionHeader from '../components/ui/section-header';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Users, Lightbulb, Code, Cpu, Award, Linkedin, Twitter, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import CardGrid from '../components/ui/card-grid';

const About: React.FC = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 md:px-10 bg-gray-900 text-white overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-[10%] w-64 h-64 bg-primary/10 rounded-full filter blur-3xl parallax"></div>
          <div className="absolute bottom-20 left-[15%] w-96 h-96 bg-secondary/10 rounded-full filter blur-3xl parallax"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] bg-accent/10 rounded-full filter blur-3xl parallax"></div>
        </div>

        <div className="container relative z-10 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Left Column - Mission Statement */}
            <div className="text-left animate-fade-in">
              <h2 className="text-2xl font-bold font-aeonik mb-4">OUR MISSION</h2>
              <p className="text-xl leading-relaxed text-white/80">
                Our mission is to empower businesses with AI that works—
                seamlessly, efficiently, and profitably. We simplify AI 
                adoption, automate workflows, and drive real business impact—
                so you can focus on growth while AI handles the rest.
              </p>
            </div>

            {/* Right Column - About Sparkwave */}
            <div className="text-left animate-fade-in">
              <h2 className="text-2xl font-bold font-aeonik mb-4">ABOUT SPARKWAVE AI</h2>
              <p className="text-xl leading-relaxed text-white/80">
                At Sparkwave AI, we specialize in practical, revenue-driven AI solutions that help businesses streamline 
                operations, increase profits, and stay ahead of the competition. We don't just build AI—we make AI work for you.
              </p>
            </div>
          </div>
        </div>
        
        {/* Gradient overlay at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent"></div>
      </section>
      
      {/* Our Values */}
      <PageSection dark className="py-24 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-[10%] w-96 h-96 bg-blue-600/5 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 left-[15%] w-64 h-64 bg-purple-600/5 rounded-full filter blur-3xl"></div>
        </div>
        
        <SectionHeader 
          badge="Our Values"
          title="What We Stand For"
          description="Our values guide everything we do – from how we design our solutions to how we interact with our clients and partners."
          centered
          className="animate-fade-in-up"
          titleClassName="font-aeonik"
          descriptionClassName="text-white/80"
          badgeClassName="text-white"
        />
        
        <CardGrid 
          darkMode
          items={[
            {
              id: "innovation",
              icon: <Cpu className="h-8 w-8 text-blue-500" />,
              title: "Innovation",
              description: "We're constantly pushing the boundaries of what's possible with AI, staying at the forefront of technological advancement."
            },
            {
              id: "collaboration",
              icon: <Users className="h-8 w-8 text-blue-500" />,
              title: "Collaboration",
              description: "We believe in working closely with our clients, treating their challenges as our own and finding solutions together."
            },
            {
              id: "excellence",
              icon: <Award className="h-8 w-8 text-blue-500" />,
              title: "Excellence",
              description: "We hold ourselves to the highest standards, ensuring that every solution we deliver exceeds expectations."
            }
          ]}
          className="animate-fade-in-up" 
          itemClassName="shadow-soft"
        />
      </PageSection>
      
      {/* CTA Section */}
      <section className="py-20 px-6 md:px-10 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="container mx-auto text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-fade-in font-aeonik">Ready to Transform Your Business with AI?</h2>
            <p className="text-lg text-gray-700 mb-8 animate-fade-in">
              Book a discovery call with our team to discuss how our AI solutions can help your business grow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up">
              <Button size="lg" className="btn-hover-effect bg-blue-600 hover:bg-blue-700 group">
                Book a Discovery Call
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Link to="/services">
                <Button variant="outline" size="lg" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                  Explore Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default About;
